import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { ParametrosService } from "app/services/parametros.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import Swal from "sweetalert2";

@Component({
  selector: "app-talonarios",
  templateUrl: "./talonarios.component.html",
  styleUrls: ["./talonarios.component.css"],
})
export class TalonariosComponent implements OnInit {
  talonarios = [];
  talonariosAgrupados: any = {};
  puntosVentaOrdenados: string[] = [];
  loading = false;

  constructor(
    private parametrosService: ParametrosService,
    private snackBar: MatSnackBar,
    private router: Router
  ) {}

  ngOnInit(): void {
    const sidebar = <HTMLElement>document.querySelector(".navbar-brand");
    if (sidebar !== undefined) {
      sidebar.innerHTML = "Talonarios";
    }
    this.getTalonarios();
  }

  getTalonarios() {
    this.loading = true;
    this.parametrosService.getTalonarios().subscribe(
      (data) => {
        this.talonarios = data;
        this.agruparPorPuntoVenta();
        this.loading = false;
      },
      (err) => {
        console.log(err);
        this.loading = false;
        this.showErrorMessage("Error al cargar los talonarios");
      }
    );
  }

  agruparPorPuntoVenta() {
    this.talonariosAgrupados = {};
    
    // Agrupar talonarios por punto de venta
    this.talonarios.forEach(talonario => {
      const puntoVenta = this.getPuntoVentaDisplay(talonario);
      
      if (!this.talonariosAgrupados[puntoVenta]) {
        this.talonariosAgrupados[puntoVenta] = [];
      }
      
      this.talonariosAgrupados[puntoVenta].push(talonario);
    });

    // Ordenar los puntos de venta alfabéticamente
    this.puntosVentaOrdenados = Object.keys(this.talonariosAgrupados).sort();
  }

  getPuntoVentaDisplay(talonario: any): string {
    if (talonario?.salesPoint?.name) {
      return talonario.salesPoint.name;
    } else if (talonario?.salesPointsId) {
      return `Punto de Venta ID: ${talonario.salesPointsId}`;
    } else {
      return 'Sin Punto de Venta';
    }
  }

  getActivosCount(talonarios: any[]): number {
    return talonarios.filter(t => t.status === true).length;
  }

  getInactivosCount(talonarios: any[]): number {
    return talonarios.filter(t => t.status === false).length;
  }

  deleteTalonario(talonarioId: number) {
    Swal.fire({
      title: '¿Está seguro?',
      text: '¿Desea eliminar este talonario? Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.parametrosService.deleteTalonario(talonarioId).subscribe(
          (data) => {
            console.log("Talonario deleted successfully", data);
            this.getTalonarios(); // Refresh the list after deletion and re-group
            this.showSuccessMessage("Talonario eliminado correctamente");
          },
          (err) => {
            console.error("Error deleting talonario", err);
            this.showErrorMessage("Error al eliminar el talonario");
          }
        );
      }
    });
  }

  toggleStatus(talonario: any) {
    const newStatus = !talonario.status;
    const action = newStatus ? 'activar' : 'desactivar';

    Swal.fire({
      title: `¿Está seguro?`,
      text: `¿Desea ${action} este talonario?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: `Sí, ${action}`,
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        // Crear objeto solo con los campos necesarios para el backend
        const updatedTalonario = {
          receiptBookTypeId: talonario.receiptBookTypeId,
          receiptBookLetterId: talonario.receiptBookLetterId || talonario.receiptBookLetter?.id,
          voucherTypeId: talonario.voucherTypeId,
          description: talonario.description,
          salesPointsId: talonario.salesPointsId || talonario.salesPoint?.id,
          isElectronic: talonario.isElectronic,
          observations: talonario.observations || '',
          fromNumber: talonario.fromNumber,
          toNumber: talonario.toNumber,
          currentNumber: talonario.currentNumber,
          status: newStatus
        };

        this.parametrosService.modificarTalonario(updatedTalonario as any, talonario.id).subscribe(
          (data) => {
            console.log("Talonario status updated successfully", data);
            talonario.status = newStatus; // Actualizar localmente
            this.showSuccessMessage(`Talonario ${action} correctamente`);
          },
          (err) => {
            console.error("Error updating talonario status", err);
            this.showErrorMessage(`Error al ${action} el talonario`);
          }
        );
      }
    });
  }

  showSuccessMessage(message: string) {
    this.snackBar.open(message, 'Cerrar', {
      duration: 3000,
      panelClass: ['success-snackbar']
    });
  }

  showErrorMessage(message: string) {
    this.snackBar.open(message, 'Cerrar', {
      duration: 4000,
      panelClass: ['error-snackbar']
    });
  }

  getTypeLabel(talonario: any): string {
    if (!talonario || !talonario.description) return 'OTRO';
    
    const description = talonario.description.toLowerCase();
    
    // Mapeo basado en la descripción del talonario
    if (description.includes('factura')) return 'VENTA';
    if (description.includes('presupuesto')) return 'VENTA';
    if (description.includes('egreso') && description.includes('caja')) return 'TESORERÍA';
    if (description.includes('ajuste') && description.includes('stock')) return 'STOCK';
    if (description.includes('tesorería')) return 'TESORERÍA';
    if (description.includes('stock')) return 'STOCK';
    
    // Fallback basado en receiptBookType
    if (talonario.receiptBookType === 0) return 'VENTA';
    if (talonario.receiptBookType === 1) return 'FISCAL';
    if (talonario.receiptBookType === 2) return 'MANUAL';
    
    return 'OTRO';
  }

  navigateToEdit(talonarioId: number, event: any) {
    // Prevenir que se active el click si se hace clic en botones dentro de la fila
    if (event && event.target && (
      event.target.closest('.dropdown-toggle') || 
      event.target.closest('.dropdown-menu') ||
      event.target.closest('button')
    )) {
      return;
    }
    
    this.router.navigate(['/configurar/talonario/edit', talonarioId]);
  }
}
